<template>
  <ToolDropdown
    :options="options"
    data-testid="tipatp-font-family-dropdown"
    tooltip="Font family"
  />
</template>

<script lang="ts" setup>
import ToolDropdown from "../_internal/ToolDropdown.vue";
import type { DropOption } from "../_internal/ToolDropdown.vue";
import {
  expandSelectionToWord,
  getActiveMarks,
} from "../_internal/cutomExtensions";
import { Editor } from "@tiptap/core";
import { computed, ref } from "vue";

const props = defineProps<{
  editor: Editor;
  fonts: {
    style: string;
    label: string;
  }[];
}>();

const previousFontFamily = ref<string | null>(null);

const activeFontFamily = computed(() => {
  setPreviousFontFamily();

  // Get marks including stored marks (for empty paragraphs after Enter)
  const marks = getActiveMarks(props.editor);
  const textStyle = marks.find((mark) => mark.type.name === "textStyle");
  if (textStyle?.attrs?.fontFamily) {
    return textStyle.attrs.fontFamily;
  }

  const { state } = props.editor;
  const { $from } = state.selection;

  const nodeFont = $from.parent.attrs?.fontFamily;
  if (nodeFont) {
    return nodeFont;
  }

  if (typeof window !== "undefined" && window.getSelection) {
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      let node = selection.anchorNode as HTMLElement | null;

      if (node?.classList?.contains("tiptap") && node.firstElementChild) {
        node = node.firstElementChild as HTMLElement;
      }

      while (node && node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
      }

      let parentEditor = node;
      let isInEditor = false;
      while (parentEditor) {
        if (parentEditor.classList.contains("tiptap-editor")) {
          isInEditor = true;
          break;
        }
        parentEditor = parentEditor.parentElement as HTMLElement | null;
      }

      if (node && isInEditor) {
        const style = window.getComputedStyle(node);
        return style.fontFamily || null;
      }
    }
  }
  return previousFontFamily.value;
});

function setPreviousFontFamily() {
  if (activeFontFamily.value) previousFontFamily.value = activeFontFamily.value;
}

const options = computed(() => {
  const fontOpt: DropOption[] = [];
  props.fonts.forEach((font) => {
    fontOpt.push({
      format: "p",
      style: `font-family: ${font.style}; font-size: 12px;`,
      text: font.label,
      set: () => {
        expandSelectionToWord(props.editor);
        props.editor.chain().focus().setFontFamily(font.style).run();
      },
      isActive: props.editor.isActive("textStyle", {
        fontFamily: font.style,
      }),
    });
  });
  const activeFontByEditor = fontOpt.find((f) => f.isActive);
  if (activeFontByEditor) {
    previousFontFamily.value = activeFontByEditor.style || "";
    return fontOpt;
  }
  if (!activeFontByEditor && activeFontFamily.value) {
    fontOpt.map((f) => {
      const styleWithoutQuot = f.style?.replace(/['"]/g, "");
      f.isActive =
        styleWithoutQuot?.startsWith(
          `font-family: ${activeFontFamily.value?.replace(/['"]/g, "").split(",")[0]},`
        ) || false;
    });
  }
  return fontOpt;
});
</script>
